import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

type AnnouncerPoliteness = 'polite' | 'assertive';

interface AnnouncerContextValue {
  announce: (message: string, politeness?: AnnouncerPoliteness) => void;
}

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null);

export const useAnnouncer = (): AnnouncerContextValue => {
  const context = useContext(AnnouncerContext);
  if (!context) {
    throw new Error('useAnnouncer must be used within an AnnouncerProvider');
  }
  return context;
};

interface AnnouncerProviderProps {
  children: React.ReactNode;
}

export const AnnouncerProvider: React.FC<AnnouncerProviderProps> = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback((message: string, politeness: AnnouncerPoliteness = 'polite') => {
    if (politeness === 'assertive') {
      setAssertiveMessage(message);
      // Clear after announcement
      setTimeout(() => setAssertiveMessage(''), 1000);
    } else {
      setPoliteMessage(message);
      // Clear after announcement
      setTimeout(() => setPoliteMessage(''), 1000);
    }
  }, []);

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      {/* Polite announcer - for non-urgent updates */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {politeMessage}
      </div>
      {/* Assertive announcer - for urgent updates */}
      <div role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
};

export default AnnouncerProvider;
